import { motion } from 'framer-motion';
import { Palette, Package, Image, BookOpen, Heart, Sparkles } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_843f7c3a-3692-4728-b14f-ba431557a9c8/artifacts/qxen4yga_Ray%20Creators%20-%20Logo.png";

const AboutPage = () => {
  const services = [
    { icon: Palette, title: 'Branding & Identity', description: 'Creating memorable brand identities that resonate with your audience and stand the test of time.' },
    { icon: Package, title: 'Packaging Design', description: 'Eye-catching packaging that tells your product\'s story and captures attention on the shelf.' },
    { icon: Image, title: 'Social Media Graphics', description: 'Scroll-stopping visuals designed to engage, inspire, and grow your online presence.' },
    { icon: BookOpen, title: 'Print Design', description: 'From brochures to flyers, we craft print materials that leave a lasting impression.' },
    { icon: Heart, title: 'Invitations', description: 'Beautiful, bespoke invitations for your special occasions and celebrations.' },
    { icon: Sparkles, title: 'Apparel Graphics', description: 'Bold, wearable art that makes a statement and elevates your brand merchandise.' },
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 hero-gradient">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.img
              src={LOGO_URL}
              alt="Ray Creators"
              className="h-24 w-24 mx-auto mb-8"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6 }}
            />
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              <span className="font-serif uppercase tracking-wider">About</span>
              <span className="font-script text-primary text-5xl md:text-7xl ml-2">Us</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto"
            >
              We are a creative design studio passionate about transforming ideas into 
              captivating visual stories that connect brands with their audiences.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 md:py-32" data-testid="story-section">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 md:order-1"
            >
              <h2 className="text-3xl md:text-4xl font-semibold mb-6">
                <span className="font-serif uppercase tracking-wider">Our</span>
                <span className="font-script text-primary text-4xl md:text-5xl ml-2">Story</span>
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Ray Creators was born from a simple belief: every brand deserves to shine. 
                  What started as a small passion project has grown into a full-fledged creative 
                  studio serving clients across industries.
                </p>
                <p>
                  Our journey began with a love for visual storytelling and a commitment to 
                  craftsmanship. Today, we continue to push creative boundaries while staying 
                  true to our roots – delivering designs that not only look beautiful but 
                  serve a purpose.
                </p>
                <p>
                  From branding to packaging, social media to print, we approach every project 
                  with fresh eyes and dedicated hearts. Because we believe that great design 
                  has the power to transform businesses and touch lives.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 md:order-2"
            >
              <div className="relative">
                <div className="aspect-square rounded-2xl overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1542744094-3a31f272c490?w=800"
                    alt="Creative workspace"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-primary rounded-full opacity-20" />
                <div className="absolute -top-6 -left-6 w-24 h-24 border-2 border-primary rounded-full" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-32 bg-muted" data-testid="values-section">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">
              <span className="font-serif uppercase tracking-wider">What We</span>
              <span className="font-script text-primary text-4xl md:text-5xl ml-2">Value</span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              The principles that guide our creative process
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'Creativity', description: 'We believe in pushing boundaries and exploring new possibilities in every project.', color: 'bg-primary' },
              { title: 'Quality', description: 'Every detail matters. We deliver work that meets the highest standards of excellence.', color: 'bg-accent' },
              { title: 'Connection', description: 'Great design creates emotional connections. We design with your audience in mind.', color: 'bg-primary' },
            ].map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-background rounded-xl p-8 text-center shadow-sm"
                data-testid={`value-${value.title.toLowerCase()}`}
              >
                <div className={`w-16 h-16 ${value.color} rounded-full mx-auto mb-6 flex items-center justify-center`}>
                  <span className="text-2xl font-bold text-white">{index + 1}</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                <p className="text-muted-foreground">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32" data-testid="services-section">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">
              <span className="font-serif uppercase tracking-wider">What We</span>
              <span className="font-script text-primary text-4xl md:text-5xl ml-2">Offer</span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Comprehensive design services tailored to your needs
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group p-6 rounded-xl border border-border hover:border-primary hover:shadow-lg transition-all duration-300"
                data-testid={`service-${index}`}
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-white transition-colors">
                  <service.icon className="h-6 w-6 text-primary group-hover:text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{service.title}</h3>
                <p className="text-muted-foreground text-sm">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
